import { apiSlice, TemplateOrCalculation } from '../../../apis/api';
import { CalculationsCollection, TemplatesCollection } from '../../../swagger/collections';
import { getTags } from '../../../apis/apiLib';
import { getSelectedProjectShortName } from '../../../apis/apiHeader';
import {
  BalancingConfiguration,
  BalancingObject,
  BalancingVersions,
  versionedEmptyBalancingObjects,
} from './balancingLib';
import cloneDeep from 'lodash.clonedeep';
import {
  CalculationConfigurationExperimentalType,
  CalculationExperimentalConfigurationRequest,
  CalculationExperimentalConfigurationResponse,
} from '@ydistri/api-sdk';
import { SignalExperimentalConfigurationChanged } from '../../../signalr/signalrInterfaces';
import { signalrClient } from '../../../signalr/client';

const { TAGS, TAGS_ARRAY } = getTags('balancingConfiguration');

export interface PatchBalancingConfigurationRequest {
  templateOrCalculation: TemplateOrCalculation;
  data: CalculationExperimentalConfigurationRequest;
}

export const apiBalancingConfiguration = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      getBalancingConfiguration: builder.query<BalancingConfiguration, TemplateOrCalculation>({
        queryFn: async arg => {
          const { data } =
            arg.type === 'Template'
              ? await TemplatesCollection.getTemplateExperimentalConfigurations(arg.id)
              : await CalculationsCollection.getCalculationExperimentalConfigurations(arg.id);

          // eslint-disable-next-line no-console -- we want to log the data
          console.log('getBalancingConfiguration DATA', data);

          const versionString = data.data.find(
            d =>
              d.key === CalculationConfigurationExperimentalType.ExperimentalConfigurationVersion,
          )?.value;

          let version = BalancingVersions.V_UNKNOWN;

          switch (versionString) {
            case BalancingVersions.V100.toString():
              version = BalancingVersions.V100;
              break;
            case BalancingVersions.V200.toString():
              version = BalancingVersions.V200;
              break;
            case undefined:
              break;
          }

          const emptyAdvancedConfigObject = versionedEmptyBalancingObjects[version];
          const result: BalancingObject = cloneDeep(emptyAdvancedConfigObject);
          data.data
            .sort((x, y) => x.itemSequence - y.itemSequence)
            .forEach(d =>
              result[d.key]?.values.push(
                result[d.key]?.type === 'Decimal' ? parseFloat(d.value) : parseInt(d.value),
              ),
            );
          // eslint-disable-next-line no-console -- we want to log the result
          console.log('RESULT', result);

          return { data: { version, configuration: result } };
        },
        providesTags: (result, error, arg) => [
          { type: TAGS.balancingConfiguration, id: `balancingConfig-${arg.type}-${arg.id}` },
        ],
        async onCacheEntryAdded({ type: objectType, id: selectedTemplateId }, api) {
          // The project this entry was created for, read before the first await: an entry survives a
          // project switch (and may finish loading after one) and a template id is not unique across
          // projects, so a signal must match the project as well as the id.
          const project = getSelectedProjectShortName();
          try {
            await api.cacheDataLoaded;

            // The Advanced values changed (the screen's own PATCH or a document apply): patch the
            // changed (key, itemSequence) cells of the open template; a calculation's copy never changes.
            const listener = (signal: SignalExperimentalConfigurationChanged) => {
              if (
                objectType !== 'Template' ||
                selectedTemplateId !== signal.templateId ||
                signal.projectSlug !== project
              ) {
                return;
              }

              api.updateCachedData(draft => {
                signal.values.forEach(({ key, itemSequence, value }) => {
                  const field = draft.configuration[key];
                  if (!field || value === null) return;
                  field.values[itemSequence] =
                    field.type === 'Decimal' ? parseFloat(value) : parseInt(value);
                });
              });
            };

            signalrClient.on('ExperimentalConfigurationChanged', listener);

            api.cacheEntryRemoved.then(() => {
              signalrClient.off('ExperimentalConfigurationChanged', listener);
            });
          } catch {
            // no-op in case cache entry was removed before data was loaded
          }
          await api.cacheEntryRemoved;
        },
      }),

      patchBalancingConfiguration: builder.mutation<
        CalculationExperimentalConfigurationResponse,
        PatchBalancingConfigurationRequest
      >({
        queryFn: async ({ templateOrCalculation, data }) => {
          const response = await TemplatesCollection.patchTemplateExperimentalConfigurations(
            templateOrCalculation.id,
            [data],
          );
          return { data: response.data.data[0] };
        },
        onQueryStarted({ templateOrCalculation, data }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            apiBalancingConfiguration.util.updateQueryData(
              'getBalancingConfiguration',
              templateOrCalculation,
              draft => {
                const key = data.key;
                /* eslint-disable typescript/no-unnecessary-condition -- fields may be undefined at runtime */
                if (
                  key !== undefined &&
                  data.value !== undefined &&
                  data.itemSequence !== undefined
                ) {
                  const draftObj = draft.configuration[key];
                  if (draftObj) {
                    draftObj.values[data.itemSequence] =
                      draft.configuration[key]?.type === 'Decimal'
                        ? parseFloat(data.value)
                        : parseInt(data.value);
                  }
                }
                /* eslint-enable typescript/no-unnecessary-condition */
              },
            ),
          );

          queryFulfilled.catch(patchResult.undo);
        },
      }),
    }),
  });

export const { useGetBalancingConfigurationQuery, usePatchBalancingConfigurationMutation } =
  apiBalancingConfiguration;
